import { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { api, getErrorMessage } from "../../api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import ConfirmDialog from "../../components/ConfirmDialog";

export default function SizeOptions() {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [newOrder, setNewOrder] = useState(0);
  const [newNote, setNewNote] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const load = () => api.get("/size-options").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post("/size-options", { name: newName.trim(), sort_order: Number(newOrder) || 0, note: newNote.trim() });
      setNewName(""); setNewOrder(0); setNewNote("");
      toast.success("Size added"); load();
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  const update = async (item, patch) => {
    try {
      await api.put(`/size-options/${item.id}`, { ...item, ...patch });
      toast.success("Saved"); load();
    } catch (err) { toast.error(getErrorMessage(err, "Save failed")); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/size-options/${id}`);
      toast.success("Deleted"); load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Delete failed"));
    } finally { setConfirmId(null); }
  };

  return (
    <>
      <PageHeader title="Sizes" subtitle="Size variants available for products (e.g. Plug, Seedling, Mature). Optional note renders in muted text next to the size." testid="page-sizes" />
      <div className="p-6 max-w-3xl">
        <form onSubmit={add} className="flex flex-wrap items-end gap-2 mb-6" data-testid="size-add-form">
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs text-[#5C665A]">Name</label>
            <Input data-testid="size-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} className="h-9" />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs text-[#5C665A]">Note <span className="text-[#5C665A]/60">(e.g. 9cm pot)</span></label>
            <Input data-testid="size-new-note" value={newNote} onChange={(e) => setNewNote(e.target.value)} className="h-9" />
          </div>
          <div className="w-24">
            <label className="text-xs text-[#5C665A]">Order</label>
            <Input data-testid="size-new-order" type="number" value={newOrder} onChange={(e) => setNewOrder(e.target.value)} className="h-9" />
          </div>
          <Button data-testid="size-add-btn" type="submit" className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white">
            <Plus size={14} /> Add
          </Button>
        </form>

        <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F9F9F8] text-[#5C665A]">
              <tr>
                <th className="text-left px-3 py-2 db-h font-semibold w-[35%]">Name</th>
                <th className="text-left px-3 py-2 db-h font-semibold">Note</th>
                <th className="text-right px-3 py-2 db-h font-semibold w-28">Sort order</th>
                <th className="px-3 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (<tr><td colSpan={4} className="px-3 py-6 text-center text-[#5C665A]" data-testid="size-empty">No sizes yet.</td></tr>)}
              {items.map((it) => (<Row key={it.id} item={it} onSave={update} onDelete={() => setConfirmId(it.id)} />))}
            </tbody>
          </table>
          </div>
        </div>
      </div>
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}
        title="Delete size?" description="Sizes in use by variants cannot be deleted."
        confirmLabel="Delete" destructive onConfirm={() => remove(confirmId)} testid="size-confirm-delete" />
    </>
  );
}

function Row({ item, onSave, onDelete }) {
  const [name, setName] = useState(item.name);
  const [order, setOrder] = useState(item.sort_order);
  const [note, setNote] = useState(item.note || "");
  const dirty = name !== item.name || Number(order) !== item.sort_order || note !== (item.note || "");
  return (
    <tr className="border-t border-[#E2E4E0] db-row" data-testid={`size-row-${item.id}`}>
      <td className="px-3 py-1.5"><input className="db-inline w-full text-left" value={name} onChange={(e) => setName(e.target.value)} data-testid={`size-name-${item.id}`} /></td>
      <td className="px-3 py-1.5"><input className="db-inline w-full text-left" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 9cm pot" data-testid={`size-note-${item.id}`} /></td>
      <td className="px-3 py-1.5 text-right"><input className="db-inline w-20" type="number" value={order} onChange={(e) => setOrder(e.target.value)} data-testid={`size-order-${item.id}`} /></td>
      <td className="px-3 py-1.5 text-right whitespace-nowrap">
        {dirty ? (
          <button className="text-[#2D6A4F] hover:text-[#1B4332] inline-flex items-center gap-1 px-2 h-7 rounded-md hover:bg-[#E9F5EC]" onClick={() => onSave(item, { name, sort_order: Number(order) || 0, note })} data-testid={`size-save-${item.id}`}>
            <Save size={14} /> Save
          </button>
        ) : (
          <button className="text-[#C94A4A] hover:bg-[#FDF3F3] inline-flex items-center px-2 h-7 rounded-md" onClick={onDelete} data-testid={`size-delete-${item.id}`}>
            <Trash2 size={14} />
          </button>
        )}
      </td>
    </tr>
  );
}
